/**
 * Catálogo de tipos de componente: factor, característica, aspecto.
 *
 * La columna real es `nombre` pero el API la expone como `tipo`.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type TipoComponente from '@/interfaces/TipoComponente'
import { getTiposComponenteService,  getTipoComponenteByIdService,  crearTipoComponenteService,  updateTipoComponenteService,  deleteTipoComponenteService } from '@/services/TipoComponenteService'

/** Store `GestionTipoComponente`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionTipoComponente = defineStore('GestionTipoComponente', {
  state: () => ({
    tipos: [] as TipoComponente[],
    tipoActual: null as TipoComponente | null,
    urlTipos: `${process.env.VUE_APP_API_BASE_URL}/api/tipos-indicador`
  }),

  actions: {
    async getTipos() {
      const data = await getTiposComponenteService(this.urlTipos)
      this.tipos = data.map(d => ({
        id_tipo_componente: d.id_tipo_componente,
        nombre: d.nombre ?? d.tipo,
        unidades: d.unidades ?? null,
        ponderacion: d.ponderacion ?? null,
        created_at: d.created_at,
        updated_at: d.updated_at
      }))
      return this.tipos
    },

    async getTipoById(id: number) {
      const data = await getTipoComponenteByIdService(this.urlTipos, id)
      if (data) this.tipoActual = data
      return data
    },

    async crearTipo(payload: Partial<TipoComponente>): Promise<TipoComponente | null> {
      const nuevo = await crearTipoComponenteService(this.urlTipos, payload)
      if (nuevo) {
        this.tipos.push(nuevo)
        this.tipoActual = nuevo
      }
      return nuevo
    },

    async updateTipo(id: number, payload: Partial<TipoComponente>) {
      const actualizado = await updateTipoComponenteService(this.urlTipos, id, payload)
      if (actualizado) this.tipoActual = actualizado
      return actualizado
    },

    async deleteTipo(id: number) {
      const eliminado = await deleteTipoComponenteService(this.urlTipos, id)
      if (eliminado) {
        this.tipos = this.tipos.filter(t => t.id_tipo_componente !== id)
        if (this.tipoActual?.id_tipo_componente === id) this.tipoActual = null
      }
      return eliminado
    }
  }
})